import { TECH_KEYWORDS } from '../config/techKeywords'
import { escapeRegex } from '../utils/stringUtils'

// Pre-compile tech keyword regexes once at module load (not per-call)
const COMPILED_TECH_REGEXES = TECH_KEYWORDS.map(tech => ({
  regex: new RegExp(`\\b(${escapeRegex(tech)})\\b`, 'g'),
}))

// ANSI-style color scheme for terminal syntax highlighting
export const TerminalColors = {
  // Standard ANSI colors
  black: '#000000',
  red: '#ff5555',
  green: '#50fa7b',
  yellow: '#f1fa8c',
  blue: '#8be9fd',
  magenta: '#ff79c6',
  cyan: '#8be9fd',
  white: '#f8f8f2',

  // Bright variants
  brightBlack: '#6272a4',
  brightRed: '#ff6e6e',
  brightGreen: '#69ff94',
  brightYellow: '#ffffa5',
  brightBlue: '#d6acff',
  brightMagenta: '#ff92df',
  brightCyan: '#a4ffff',
  brightWhite: '#ffffff',

  // Syntax-specific colors
  comment: '#6272a4',
  string: '#50fa7b',
  number: '#bd93f9',
  boolean: '#ff79c6',
  keyword: '#8be9fd',
  operator: '#ff79c6',
  punctuation: '#f8f8f2',
  function: '#50fa7b',
  variable: '#f8f8f2',
  property: '#8be9fd',
} as const

export type ColorName = keyof typeof TerminalColors

// Generate inline style for colored text
export function colorize(text: string, color: ColorName): string {
  return `<span class="syntax-${color}">${escapeHtml(text)}</span>`
}

// Generate multiple colored segments
export function colorizeSegments(segments: Array<{ text: string; color: ColorName }>): string {
  return segments.map(seg => colorize(seg.text, seg.color)).join('')
}

// Escape HTML to prevent XSS
function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

// Detect file type from extension
export function detectFileType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase()

  const typeMap: Record<string, string> = {
    // Code files
    js: 'javascript',
    jsx: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    py: 'python',
    rb: 'ruby',
    go: 'go',
    rs: 'rust',
    java: 'java',
    cpp: 'cpp',
    c: 'c',
    h: 'c',
    cs: 'csharp',
    php: 'php',
    swift: 'swift',
    kt: 'kotlin',

    // Data/Config files
    json: 'json',
    yaml: 'yaml',
    yml: 'yaml',
    toml: 'toml',
    xml: 'xml',
    csv: 'csv',

    // Markup/Docs
    md: 'markdown',
    markdown: 'markdown',
    html: 'html',
    htm: 'html',
    css: 'css',
    scss: 'scss',

    // Shell/Config
    sh: 'shell',
    bash: 'shell',
    zsh: 'shell',
    fish: 'shell',

    // Other
    txt: 'text',
    log: 'log',
  }

  return ext ? typeMap[ext] || 'text' : 'text'
}

// Color coding for file types in ls output
export function getFileTypeColor(filename: string, isDirectory: boolean): ColorName {
  if (isDirectory) return 'brightBlue'

  const fileType = detectFileType(filename)

  const colorMap: Record<string, ColorName> = {
    javascript: 'brightYellow',
    typescript: 'blue',
    python: 'brightGreen',
    ruby: 'red',
    go: 'cyan',
    rust: 'brightRed',
    json: 'yellow',
    markdown: 'brightCyan',
    html: 'magenta',
    css: 'brightMagenta',
    shell: 'green',
    text: 'white',
    log: 'brightBlack',
  }

  return colorMap[fileType] || 'white'
}

// Format help/hint text with colored commands and placeholders
export function formatHelpText(text: string): string {
  let formatted = text

  // Highlight command names (words before spaces or within backticks)
  // Match patterns like: `command` or 'command' or command at start of line/after Type
  formatted = formatted.replace(
    /`([a-z_-]+)`/g,
    (_, cmd) => colorize(cmd, 'cyan')
  )

  // Highlight placeholders in square brackets [like this]
  formatted = formatted.replace(
    /\[([^\]]+)\]/g,
    (_, placeholder) => colorize(`[${placeholder}]`, 'yellow')
  )

  // Highlight option flags like -a, --help
  formatted = formatted.replace(
    /\B(-{1,2}[a-z]+)\b/gi,
    (flag) => colorize(flag, 'magenta')
  )

  return formatted
}

// Semantic formatting utilities
export function formatURL(url: string): string {
  return colorize(url, 'cyan')
}

export function formatEmail(email: string): string {
  return colorize(email, 'cyan')
}

export function formatPath(path: string): string {
  return colorize(path, 'yellow')
}

export function formatDate(date: string): string {
  return colorize(date, 'brightBlack')
}

export function formatNumber(num: string | number): string {
  return colorize(String(num), 'magenta')
}

export function formatTechnology(tech: string): string {
  return colorize(tech, 'brightGreen')
}

export function formatCommand(command: string): string {
  return colorize(command, 'cyan')
}

export function formatSuccess(text: string): string {
  return colorize(text, 'green')
}

export function formatError(text: string): string {
  return colorize(text, 'red')
}

export function formatWarning(text: string): string {
  return colorize(text, 'yellow')
}

export function formatHighlight(text: string): string {
  return colorize(text, 'brightCyan')
}

// Auto-format text with semantic patterns
export function autoFormatText(text: string): string {
  let formatted = text

  // URLs
  formatted = formatted.replace(
    /(https?:\/\/[^\s]+)/g,
    (url) => formatURL(url)
  )

  // Email addresses
  formatted = formatted.replace(
    /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g,
    (email) => formatEmail(email)
  )

  // File paths (~/path or /path or ./path)
  formatted = formatted.replace(
    /\B(~\/[\w/.-]+|\b\.\/[\w/.-]+|\b\/[\w/.-]+)/g,
    (path) => formatPath(path)
  )

  // Dates (YYYY-MM-DD or DD/MM/YYYY)
  formatted = formatted.replace(
    /\b(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})\b/g,
    (date) => formatDate(date)
  )

  // Numbers (including versions like 1.0.0)
  formatted = formatted.replace(
    /\b(\d+\.?\d*)\b/g,
    (num) => formatNumber(num)
  )

  // Commands in backticks
  formatted = formatted.replace(
    /`([^`]+)`/g,
    (_, cmd) => formatCommand(cmd)
  )

  // Placeholders in square brackets
  formatted = formatted.replace(
    /\[([^\]]+)\]/g,
    (_, placeholder) => colorize(`[${placeholder}]`, 'yellow')
  )

  // Success indicators
  formatted = formatted.replace(
    /\b(✓|SUCCESS|OK|Passed|Success|success|successful)\b/gi,
    (match) => formatSuccess(match)
  )

  // Error indicators
  formatted = formatted.replace(
    /\b(✗|ERROR|FAIL|Failed|Error|error|failed)\b/gi,
    (match) => formatError(match)
  )

  // Warning indicators
  formatted = formatted.replace(
    /\b(WARNING|WARN|Deprecated|warning)\b/gi,
    (match) => formatWarning(match)
  )

  return formatted
}

// Format command output with semantic highlighting
export function formatCommandOutput(text: string, options: {
  highlightTech?: boolean
  highlightDates?: boolean
  highlightNumbers?: boolean
  highlightPaths?: boolean
  highlightUrls?: boolean
} = {}): string {
  const {
    highlightTech = true,
    highlightDates = true,
    highlightNumbers = true,
    highlightPaths = true,
    highlightUrls = true,
  } = options

  let formatted = text

  if (highlightUrls) {
    // URLs
    formatted = formatted.replace(
      /(https?:\/\/[^\s]+)/g,
      (url) => formatURL(url)
    )

    // Email addresses
    formatted = formatted.replace(
      /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g,
      (email) => formatEmail(email)
    )
  }

  if (highlightPaths) {
    // File paths
    formatted = formatted.replace(
      /\B(~\/[\w/.-]+|\b\.\/[\w/.-]+|\b\/[\w/.-]+)/g,
      (path) => formatPath(path)
    )
  }

  if (highlightDates) {
    // Dates
    formatted = formatted.replace(
      /\b(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]* \d{4})\b/gi,
      (date) => formatDate(date)
    )
  }

  if (highlightNumbers) {
    // Version numbers
    formatted = formatted.replace(
      /\b(v?\d+\.\d+(?:\.\d+)?)\b/g,
      (version) => formatNumber(version)
    )
  }

  if (highlightTech) {
    // Use pre-compiled regexes (avoids creating 141 RegExp objects per call)
    COMPILED_TECH_REGEXES.forEach(({ regex }) => {
      regex.lastIndex = 0
      formatted = formatted.replace(regex, (match) => formatTechnology(match))
    })
  }

  // Commands in backticks
  formatted = formatted.replace(
    /`([^`]+)`/g,
    (_, cmd) => formatCommand(cmd)
  )

  // Placeholders in square brackets
  formatted = formatted.replace(
    /\[([^\]]+)\]/g,
    (_, placeholder) => colorize(`[${placeholder}]`, 'yellow')
  )

  return formatted
}
